import { prenatalScopeFrom } from './prenatalScope';
import React, { useContext } from 'react';
import Grid from '@mui/material/Grid';
import ModalButtons from 'src/components/UI/button/ModalButtons';
import { APICONSTANTS } from 'src/components/Constants';
import axios from 'src/axios';

import { Box, FormControl, TextField, Switch, Typography } from '@mui/material';

import { calculateAge, formatDate } from 'src/util/util';
import { SnackContext, AppContext } from 'src/store/ContextStore';
import { getConsentTags, SAMPLE_TYPES } from './formService';
import { ThemeContext, UserContext } from 'src/store/ContextStore';
import { getDependencies } from './formService';

import TextInput from 'src/components/UI/input/TextInput';
import ClinicalNoteDeleteModal from '../../diagnoses-menu/ClinicalNoteDeleteModal';
import FormLabel from 'src/components/UI/input/CustomFormLabel';
import DateInput from 'src/components/UI/input/DateInput';
import AssociatedNote from 'src/components/UI/input/AssociatedNote';

export default function SampleForm({
  data,
  handleClose,
  familyMemberId,
  isEditing,
  model,
  staffMembers,
  staffMemberOptions,
  setUnsavedChanges
}) {
  const { content } = React.useContext(AppContext);
  const { pedigreeSelectedUsers: users } = content;
  const { selectedUser: user } = users;
  const { theme } = useContext(ThemeContext);
  const { user: loggedInUser } = React.useContext(UserContext);

  const patientDob = data?.dob;
  const computeAgeAtRecord = React.useCallback(
    (dateTaken) => {
      if (!patientDob || !dateTaken) return '';
      const parsedDate = new Date(dateTaken);
      if (Number.isNaN(parsedDate.getTime())) return '';
      return calculateAge(patientDob, parsedDate);
    },
    [patientDob]
  );

  const defaultDateTaken = new Date();
  const defaultValues = isEditing
    ? {
        ...model,
        ageAtRecord: model.ageAtRecord,
        obtainedBy: Array.isArray(model.obtainedBy)
          ? model.obtainedBy.map((r) => r.userId || r._id).filter(Boolean)
          : []
      }
    : {
        // PRENATAL SCOPE (decision 21) — see prenatalScope.js. Empty for an ordinary record.
        ...prenatalScopeFrom(model),
        familyId: user.familyId,
        familyMemberId: familyMemberId,
        sampleType: '',
        dateTaken: defaultDateTaken,
        trackingId: '',
        sampleStatus: '',
        ageAtRecord: computeAgeAtRecord(defaultDateTaken),
        associatedConsent: { label: 'none', _id: '' },
        obtainedBy: loggedInUser._id ? [loggedInUser._id] : [],
        reminderEnabled: false,
        reminderDate: null
      };

  const [formValues, setFormValues] = React.useState(defaultValues);

  const allStaffMembers = React.useMemo(() => {
    return staffMemberOptions.map((options) => options.value);
  }, [staffMemberOptions]);

  const staffMembersMap = React.useMemo(() => {
    return staffMemberOptions.reduce((acc, item) => {
      acc[item.value] = item.label;
      return acc;
    }, {});
  }, [staffMemberOptions]);
  const [reminderTouched, setReminderTouched] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState({});

  const computeDefaultReminderDate = React.useCallback((taken) => {
    return taken ? new Date(taken) : new Date();
  }, []);

  const handleChange = (e) => {
    if (e.target.name === 'dateTaken') {
      const nextDate = e.target.value;
      if (!formValues.reminderEnabled || !reminderTouched) {
        const nextDefault = computeDefaultReminderDate(nextDate);
        setFormValues((prev) => ({ ...prev, reminderDate: nextDefault }));
      }
      setFormValues((prevValues) => ({
        ...prevValues,
        ageAtRecord: computeAgeAtRecord(nextDate)
      }));
    }

    if (e.target.type === 'date') {
      const selectedDate = new Date(e.target.value);
      const today = new Date();

      if (selectedDate >= today && e.target.name !== 'reminderDate') {
        setError((prev) => ({ ...prev, [e.target.name]: true }));
      } else setError({});
    }
    setFormValues((prevValues) => ({
      ...prevValues,
      [e.target.name]: e.target.value
    }));
    setUnsavedChanges((prev) => ({ ...prev, edit: true }));
  };

  const { setSnack } = React.useContext(SnackContext);
  let [consentOptions, setConsentOptions] = React.useState(['loading...']);
  React.useEffect(() => {
    const fetchData = async () => {
      const consentTags = await getConsentTags(
        theme,
        familyMemberId,
        formValues.familyId
      );
      setConsentOptions(consentTags);
    };
    if (consentOptions != [] && consentOptions[0] == 'loading...') fetchData();
  }, []);

  React.useEffect(() => {
    if (!patientDob) return;
    const nextAge = computeAgeAtRecord(formValues.dateTaken);
    setFormValues((prev) => {
      const currentAge = prev.ageAtRecord === undefined ? '' : prev.ageAtRecord;
      if (nextAge === '' && currentAge === '') return prev;
      if (nextAge === currentAge) return prev;
      return { ...prev, ageAtRecord: nextAge };
    });
  }, [patientDob, computeAgeAtRecord, formValues.dateTaken]);

  const handleCloseModal = () => {
    setOpen(false);
  };
  const handleOpenModal = () => {
    setOpen(true);
  };

  const deleteNote = async (e) => {
    setLoading(true);

    const dep = await getDependencies(
      user.familyId,
      model._id,
      theme.dateFormat
    );
    if (dep.length !== 0) {
      setSnack({
        status: true,
        msg: `Associated record exists; check the ${dep[0]}`,
        severity: 'error'
      });
      setLoading(false);
      return;
    }
    axios
      .delete(`/clinical-notes/note`, {
        params: {
          _id: model._id
        }
      })
      .then((res) => {
        setSnack({
          status: true,
          msg: 'Record deleted',
          severity: 'success'
        });
        handleCloseModal();
        handleClose({ exit: true });
      })
      .catch((err) => {
        setSnack({
          status: true,
          msg: `Delete failed! ${err}`,
          severity: 'error'
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const validateForm = (values) => {
    let errors = {};

    if (values.sampleType === '') {
      errors.sampleType = 'Sample Type is required.';
    }
    if (values.dateTaken === '') {
      errors.dateTaken = 'Date Taken is required.';
    }

    if (!values.sampleHeldBy) {
      errors.sampleHeldBy = 'Sample Held By is required.';
    }

    if (values.dateTaken) {
      const selectedDate = new Date(values.dateTaken);
      const today = new Date();

      if (selectedDate > today) {
        errors.dateTaken = 'Date Taken cannot be in future';
      }
    }

    setError(errors);
    return errors;
  };

  //Think about this
  const handleSubmit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const errors = validateForm(formValues);

    if (Object.keys(errors).length > 0) return Promise.reject();
    const obtainedByPayload =
      formValues.obtainedBy && formValues.obtainedBy.length > 0
        ? formValues.obtainedBy
            .map((id) => staffMembers.find((member) => member.userId === id))
            .filter(Boolean)
        : [];

    const parsePayload = {
      ...formValues,
      obtainedBy: obtainedByPayload
    };

    return axios
      .put(APICONSTANTS.sample, parsePayload)
      .then((res) => {
        handleClose({ exit: true });
        if (res.status === 200) {
          setSnack({
            status: true,
            msg: 'Successfully saved',
            severity: 'success'
          });
        } else {
          setSnack({
            status: true,
            msg: `Upload failed!`,
            severity: 'error'
          });
        }
      })
      .catch((err) => {
        setSnack({
          status: true,
          msg: `Upload failed! ${err}`,
          severity: 'error'
        });
      });
  };

  const handleGenerateTrackingId = async () => {
    try {
      const res = await axios.put(APICONSTANTS.sample, {
        familyId: formValues.familyId,
        familyMemberId: formValues.familyMemberId,
        dateTaken: formValues.dateTaken,
        generateTrackingIdOnly: true
      });
      if (res?.data?.trackingId) {
        handleChange({
          target: { name: 'trackingId', value: res.data.trackingId }
        });
      }
    } catch (err) {
      setSnack({
        status: true,
        msg: `Failed to generate Tracking ID`,
        severity: 'error'
      });
    }
  };

  return (
    //Should be fine to reuse old class?
    <form className="user-form">
      <ClinicalNoteDeleteModal
        loading={loading}
        open={open}
        handleCloseModal={handleCloseModal}
        onConfirm={deleteNote}
        title="Are you sure you want to delete this Sample ?"
        content="Warning: This action will permanently delete this Sample. Once deleted, this
      information will be permanently removed and cannot be restored."
      />
      <Grid container spacing={2}>
        {/* Sample Type  */}
        <Grid size={12}>
          <TextInput
            label="Sample type"
            variant="outlined"
            type={'autocomplete'}
            fullWidth
            required
            onChange={(e) => {
              const label = e ? e.label : '';
              handleChange({ target: { name: 'sampleType', value: label } });
            }}
            name="sampleType"
            options={SAMPLE_TYPES}
            value={formValues.sampleType}
            validated={!!error.sampleType}
            helperText={error.sampleType}
          />
        </Grid>
        {/* Date Taken (Mandatory) & Associated Consent */}
        <Grid size={6}>
          <Grid sx={{ paddingBottom: 1 }}>
            <FormLabel required>Date Taken </FormLabel>
          </Grid>
          <DateInput
            name="dateTaken"
            value={formValues.dateTaken ? new Date(formValues.dateTaken) : null}
            disableFuture
            onChange={(val) =>
              handleChange({
                target: { name: 'dateTaken', value: val, type: 'date' }
              })
            }
            variant="outline"
            error={!!error.dateTaken}
            helperText={error.dateTaken}
          />
        </Grid>
        <Grid size={6}>
          <TextInput
            label="Age at Sample"
            name="ageAtRecord"
            type="number"
            value={formValues.ageAtRecord ?? ''}
            inputProps={{ min: 0, className: 'no-spinners' }}
            // Disabled (muted) only when an age is actually being
            // auto-calculated, i.e. DoB AND date taken both set.
            // Editable when either is missing so a known age can be
            // recorded directly.
            disabled={!!patientDob && !!formValues.dateTaken}
            onChange={
              patientDob && formValues.dateTaken
                ? undefined
                : (e) => {
                    const val = e.target.value;
                    const parsed = val === '' ? '' : Number(val);
                    if (val === '' || !Number.isNaN(parsed)) {
                      setFormValues((prev) => ({
                        ...prev,
                        ageAtRecord: val === '' ? '' : parsed
                      }));
                      setUnsavedChanges((prev) => ({ ...prev, edit: true }));
                    }
                  }
            }
          />
        </Grid>
        <Grid size={6}>
          <Grid sx={{ paddingBottom: 1 }}>
            <FormLabel>Associated Consent</FormLabel>
          </Grid>
          <AssociatedNote
            name="associatedConsent"
            handleChange={handleChange}
            value={formValues.associatedConsent}
            noteOptions={consentOptions}
          />
        </Grid>
        <Grid size={6}>
          <Grid sx={{ paddingBottom: 1 }}>
            <FormLabel required>Sample Held By</FormLabel>
          </Grid>
          <TextField
            variant="outlined"
            fullWidth
            name="sampleHeldBy"
            value={formValues.sampleHeldBy || ''}
            onChange={handleChange}
            error={!!error.sampleHeldBy}
            helperText={error.sampleHeldBy}
          />
        </Grid>
        {/* Tracking ID */}
        <Grid size={12}>
          <Grid
            sx={{
              paddingBottom: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            <FormLabel>Tracking ID</FormLabel>
            <Typography
              component="a"
              onClick={handleGenerateTrackingId}
              sx={{
                fontSize: 14,
                fontWeight: 600,
                color: theme?.accent || 'primary.main',
                cursor: 'pointer',
                textDecoration: 'none',
                '&:hover': { opacity: 0.8 }
              }}
            >
              Generate ID
            </Typography>
          </Grid>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              variant="outlined"
              fullWidth
              onChange={handleChange}
              name="trackingId"
              value={formValues.trackingId}
              sx={{ flex: 1 }}
            />
          </Box>
        </Grid>
        {/* Sample Status & Status Date */}
        <Grid size={6}>
          <Grid sx={{ paddingBottom: 1 }}>
            <FormLabel>Sample Status</FormLabel>
          </Grid>
          <Box sx={{ display: 'flex', position: 'relative' }}>
            <TextField
              variant="outlined"
              fullWidth
              onChange={handleChange}
              name="sampleStatus"
              value={formValues.sampleStatus}
            />
          </Box>
        </Grid>
        <Grid size={6}>
          <Grid sx={{ paddingBottom: 1 }}>
            <FormLabel>Status Date</FormLabel>
          </Grid>
          <DateInput
            name="statusDate"
            value={
              formValues.statusDate ? new Date(formValues.statusDate) : null
            }
            onChange={(val) =>
              handleChange({
                target: { name: 'statusDate', value: val, type: 'date' }
              })
            }
            variant="outline"
          />
        </Grid>

        {/* Obtained By */}
        <Grid size={12}>
          <FormControl component="fieldset" fullWidth>
            <Grid container spacing={1}>
              <Grid size={12}>
                <TextInput
                  label={'Obtained By'}
                  name="obtainedBy"
                  type="multiple-select"
                  value={formValues?.obtainedBy || []}
                  onChange={handleChange}
                  options={allStaffMembers || []}
                  mapper={staffMembersMap || {}}
                />
              </Grid>
            </Grid>
          </FormControl>
        </Grid>

        <Grid size={12}>
          <Grid
            container
            spacing={2}
            sx={{
              alignItems: 'center'
            }}
          >
            <Grid
              size={{
                xs: 12,
                sm: 'auto'
              }}
            >
              <FormLabel>Reminder</FormLabel>
            </Grid>
            <Grid>
              <Switch
                checked={!!formValues.reminderEnabled}
                onChange={(e) => {
                  const enabled = e.target.checked;
                  setFormValues((prev) => {
                    const next = { ...prev, reminderEnabled: enabled };
                    if (enabled && !prev.reminderDate) {
                      next.reminderDate = computeDefaultReminderDate(
                        prev.dateTaken
                      );
                    }
                    return next;
                  });
                  setUnsavedChanges((prev) => ({ ...prev, edit: true }));
                }}
                name="reminderEnabled"
                color="secondary"
              />
            </Grid>
            {formValues.reminderEnabled && (
              <Grid
                sx={{ marginLeft: 14 }}
                size={{
                  xs: 12,
                  sm: 6
                }}
              >
                <Grid sx={{ paddingBottom: 1 }}>
                  <FormLabel>Reminder Date</FormLabel>
                </Grid>
                <DateInput
                  name="reminderDate"
                  value={formValues.reminderDate}
                  // you can allow future reminders; keep min at dateTaken if you want to nudge users
                  minDate={formValues.dateTaken || new Date()}
                  onChange={(val) => {
                    setReminderTouched(true);
                    handleChange({
                      target: { name: 'reminderDate', value: val, type: 'date' }
                    });
                  }}
                  variant="outline"
                  helperText={'Defaults to Date Taken'}
                />
              </Grid>
            )}
          </Grid>
        </Grid>

        {/* Modal Buttons */}
        <ModalButtons
          isEditing={isEditing}
          hideContinueAdding={true}
          onClick={handleSubmit}
          handleClose={handleClose}
          intermediateBtnLabel="Delete"
          intermediateAction={isEditing ? handleOpenModal : null}
          addLabel="Save new sample "
        />
      </Grid>
    </form>
  );
}
